import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '@/mocks/blogPosts';

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(el); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}

function RevealSection({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export default function LandingBlog() {
  return (
    <section id="blog" className="py-16 md:py-24 px-5 md:px-8 bg-background-100 dark:bg-background-100">
      <div className="max-w-7xl mx-auto">
        <RevealSection>
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-bold text-secondary-600 dark:text-secondary-400 uppercase tracking-[0.2em] mb-3">Blog</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-4">
              Dicas, tutoriais e novidades<br />do WhatsApp Marketing
            </h2>
            <p className="text-foreground-500 dark:text-foreground-400 max-w-xl mx-auto text-base">
              Conteúdo atualizado para você extrair o máximo da plataforma.
            </p>
          </div>
        </RevealSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {blogPosts.map((post, i) => (
            <RevealSection key={i} delay={i * 100}>
              <article className="bg-white dark:bg-background-50 rounded-2xl border border-background-200 dark:border-background-300 overflow-hidden group hover:border-primary-200 dark:hover:border-primary-500/30 transition-all duration-300 h-full flex flex-col">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 rounded-full bg-white/90 dark:bg-background-100/90 backdrop-blur-sm text-[10px] font-bold text-foreground-700 dark:text-foreground-300">
                      {post.category}
                    </span>
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-[11px] text-foreground-400 mb-3">
                    <span>{post.date}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300" />
                    <span>{post.readTime} de leitura</span>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-950 dark:text-foreground-100 mb-2 leading-snug group-hover:text-primary-600 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-sm text-foreground-500 dark:text-foreground-400 leading-relaxed flex-1">
                    {post.excerpt}
                  </p>
                  <div className="mt-4 pt-4 border-t border-background-200 dark:border-background-300">
                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      Ler artigo <i className="ri-arrow-right-line text-xs" />
                    </Link>
                  </div>
                </div>
              </article>
            </RevealSection>
          ))}
        </div>
      </div>
    </section>
  );
}